import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronLeft, Loader2 } from 'lucide-react';
import { getLostReasons, type CardStage, type LostReason } from '@/api/crmCard';
import { Sheet } from '@/components/Sheet';
import { crmKeys } from '@/hooks/useDealCard';
import { splitStages, stageColor } from '@/lib/dealCard';

interface Props {
  project: string;
  pipelineName: string;
  stages: CardStage[];
  currentId: string;
  /** Выбрана обычная стадия. Окно закрывается сразу — результат показывает полоса (с откатом при отказе). */
  onPick: (stage: CardStage) => void;
  /** Отказ: этап отказа и причина из справочника CRM. */
  onLost: (stage: CardStage, reason: LostReason, comment: string) => void;
  onClose: () => void;
  /** Открыть сразу на причинах отказа («Отсеять» лид из списка «Воронки»). */
  startAsking?: boolean;
}

/**
 * Окно «Стадия сделки» (прототип, stageSheet): стадии воронки сделки + «Отказ», который просит причину.
 * Причины — справочник CRM (GET sales/reasons); у части причин обязателен комментарий (needsComment).
 */
export function StageSheet({ project, pipelineName, stages, currentId, onPick, onLost, onClose, startAsking = false }: Props) {
  const { regular, lost } = splitStages(stages);
  const [asking, setAsking] = useState(startAsking);
  const [reason, setReason] = useState<LostReason | null>(null);
  const [comment, setComment] = useState('');
  const reasons = useQuery({ queryKey: crmKeys.reasons(project), queryFn: getLostReasons, enabled: asking, staleTime: 10 * 60_000 });
  const target = lost.find((s) => s.id === currentId) ?? lost[0];

  const canSend = !!reason && (!reason.needsComment || comment.trim().length > 0);

  return (
    <Sheet label="Стадия" onClose={onClose}>
      <div className="sh">
        {asking && <button type="button" className="ibtn" onClick={() => setAsking(false)} aria-label="Назад к стадиям"><ChevronLeft aria-hidden /></button>}
        <h3>{asking ? 'Причина отказа' : 'Стадия сделки'}</h3>
        <span className="shsub">{pipelineName}</span>
      </div>
      <div className="sb" style={{ gap: 2 }}>
        {!asking && (
          <>
            {regular.map((s) => (
              <button key={s.id} type="button" className={`opt${s.id === currentId ? ' on' : ''}`} onClick={() => (s.id === currentId ? onClose() : onPick(s))}>
                <span className="sdot" style={{ background: stageColor(s) }} />
                <span className="t">{s.name}</span>
                {s.id === currentId && <Check aria-hidden />}
              </button>
            ))}
            {target && (
              <button type="button" className="opt" onClick={() => setAsking(true)}>
                <span className="sdot" style={{ background: 'var(--danger)' }} />
                <span className="t">{target.name === 'Отказ' || !target.name ? 'Отказ' : target.name}<small>Попросим указать причину</small></span>
              </button>
            )}
          </>
        )}

        {asking && (
          <>
            {reasons.isPending && <div className="empty" role="status"><span className="spinner size-7 inline-block" /></div>}
            {reasons.isError && <p className="empty" role="alert">Не удалось загрузить причины: {reasons.error.message}</p>}
            {reasons.data?.map((r) => (
              <button key={r.id} type="button" className={`opt${reason?.id === r.id ? ' on' : ''}`} onClick={() => setReason(r)} aria-pressed={reason?.id === r.id}>
                <span className="t">{r.name}{r.needsComment ? <small>Нужен комментарий</small> : null}</span>
                {reason?.id === r.id && <Check aria-hidden />}
              </button>
            ))}
            {reason && (
              <label className="ifield" style={{ marginTop: 10 }}>
                <span>{reason.needsComment ? 'Комментарий (обязательно)' : 'Комментарий (по желанию)'}</span>
                <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
              </label>
            )}
            <div className="btnrow" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="primary danger"
                disabled={!canSend || !target}
                onClick={() => target && reason && onLost(target, reason, comment.trim())}
              >
                {reasons.isFetching ? <Loader2 className="spin-i" aria-hidden /> : null}Отказ
              </button>
            </div>
          </>
        )}
      </div>
    </Sheet>
  );
}
